// library.ts — the LOCAL lesson library (agent layer, DIRECTION-GENERAL-SPEAKING.md
// addendum 2026-08-12). Lessons, study plans and notes the assistant builds or the
// customer saves, organized in named folders — controlled entirely through chat/voice
// ("save that as a lesson", "what's in my library?", "remove the grammar one",
// "move it to Plans", "merge these two").
//
// STORAGE: device-only localStorage, same policy as the chat and practice history —
// nothing here ever reaches the Oriora server. Pushing an item to the customer's own
// GitHub/Drive rides the existing save_to_cloud relay ONLY when they ask.
export interface LibraryItem {
  id: string;
  title: string;
  kind: "lesson" | "plan" | "note";
  folder: string;          // free-form label; "" = unfiled
  content: string;
  created_at: string;
  updated_at: string;
}

const KEY = "oriora.speaking.library.v1";
const MAX_ITEMS = 500;

function load(): LibraryItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) { const p = JSON.parse(raw); if (Array.isArray(p)) return p as LibraryItem[]; }
  } catch { /* fresh start */ }
  return [];
}
function save(items: LibraryItem[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items.slice(-MAX_ITEMS))); } catch { /* full — oldest lost on next write */ }
}

const now = () => new Date().toISOString();
const newId = () => `lib-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export function libraryAdd(title: string, kind: LibraryItem["kind"], content: string, folder = ""): LibraryItem {
  const items = load();
  const item: LibraryItem = { id: newId(), title: title.trim() || "Untitled", kind, folder: folder.trim(), content, created_at: now(), updated_at: now() };
  items.push(item);
  save(items);
  return item;
}

export function libraryList(folder?: string): LibraryItem[] {
  const items = load();
  return folder === undefined ? items : items.filter((i) => i.folder.toLowerCase() === folder.trim().toLowerCase());
}

/** Fuzzy title match — same contract as session topic resolution: 0, 1, or many. */
export function libraryFind(term: string): LibraryItem[] {
  const t = term.trim().toLowerCase();
  if (!t) return [];
  const items = load();
  const exact = items.filter((i) => i.title.toLowerCase() === t);
  if (exact.length) return exact;
  return items.filter((i) => i.title.toLowerCase().includes(t) || t.includes(i.title.toLowerCase()));
}

export function libraryRemove(id: string): boolean {
  const items = load();
  const next = items.filter((i) => i.id !== id);
  if (next.length === items.length) return false;
  save(next);
  return true;
}

export function libraryMove(id: string, folder: string): boolean {
  const items = load();
  const it = items.find((i) => i.id === id);
  if (!it) return false;
  it.folder = folder.trim();
  it.updated_at = now();
  save(items);
  return true;
}

/** Merge N items into one (contents concatenated in given order); originals removed. */
export function libraryMerge(ids: string[], title: string): LibraryItem | null {
  const items = load();
  const parts = ids.map((id) => items.find((i) => i.id === id)).filter((x): x is LibraryItem => !!x);
  if (parts.length < 2) return null;
  const merged: LibraryItem = {
    id: newId(),
    title: title.trim() || parts.map((p) => p.title).join(" + "),
    kind: parts.some((p) => p.kind === "plan") ? "plan" : parts[0].kind,
    folder: parts[0].folder,
    content: parts.map((p) => `## ${p.title}\n\n${p.content}`).join("\n\n---\n\n"),
    created_at: now(),
    updated_at: now(),
  };
  const keep = items.filter((i) => !ids.includes(i.id));
  keep.push(merged);
  save(keep);
  return merged;
}

/** One-line inventory for the brain's context — titles only, never content. */
export function librarySummary(): string {
  const items = load();
  if (!items.length) return "(library empty)";
  return items.slice(-30).map((i) => `"${i.title}" (${i.kind}${i.folder ? ", folder: " + i.folder : ""})`).join(" · ");
}
